import { useEffect, useState } from 'react';
import './App.css';
import logo from "./Assets/Images/flexGamesLogo.png";
import UISidebar from '../Domain/Components/UI/Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiceSix, faSackDollar, faUser, faGift } from '@fortawesome/free-solid-svg-icons';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import UINavbar from '../Domain/Components/UI/Navbar/Navbar';
import GameConfigListPage from './Pages/GameConfig/GameConfigList';
import GameConfigDetailsPage from './Pages/GameConfig/GameConfigDetails';
import CurrencyRateListPage from './Pages/CurrencyRate/CurrencyRateList';
import CurrencyRateDetailsPage from './Pages/CurrencyRate/CurrencyRateDetails';
import GameSessionListPage from './Pages/GameSession/GameSessionList';
import GameSessionDetailsPage from './Pages/GameSession/GameSessionDetails';
import LoginPage from './Pages/Login/LoginPage';
import { getWithExpiry } from '../Domain/Helpers/LocalStorageHelper';
import TransactionListPage from './Pages/Transaction/TransactionList';
import TransactionDetailsPage from './Pages/Transaction/TransactionDetails';
import CampaignListPage from './Pages/Campaign/CampaignList';
import CampaignDetailsPage from './Pages/Campaign/CampaignDetails';
import TenantListPage from './Pages/Tenant/TenantList';
import TenantDetailsPage from './Pages/Tenant/TenantDetails';
import JackpotListPage from './Pages/Jackpot/JackpotList';
import JackpotDetailsPage from './Pages/Jackpot/JackpotDetails';
import CampaignOperatorGameConfigListPage from './Pages/CampaignOperatorGameConfig/CampaignOperatorGameConfigList';
import CampaignOperatorGameConfigDetailsPage from './Pages/CampaignOperatorGameConfig/CampaignOperatorGameConfigDetails';
import FreeSpinOfferListPage from './Pages/FreeSpinOffer/FreeSpinOfferList';
import FreeSpinOfferDetailsPage from './Pages/FreeSpinOffer/FreeSpinOfferDetails';
import CampaignPlayerCurrencyTransactionListPage from './Pages/CampaignPlayerCurrencyTransaction/CampaignPlayerCurrencyTransactionList';
import CampaignPlayerCurrencyTransactionDetailsPage from './Pages/CampaignPlayerCurrencyTransaction/CampaignPlayerCurrencyTransactionDetails';
import CampaignPlayerListPage from './Pages/CampaignPlayer/CampaignPlayerList';
import CampaignPlayerDetailsPage from './Pages/CampaignPlayer/CampaignPlayerDetails';
import { logoutCall } from '../Infrastructure/Services/AuthService';
import { ToastContainer } from 'react-toastify';
import GameReportPage from './Pages/GameReport/GameReportPage';
import { faFile } from '@fortawesome/free-solid-svg-icons/faFile';

const ROLE_ACCESS = {
  'super-admin':  { sections: ['Financial', 'Gaming', 'Reports', 'Account'], gamingPaths: null },
  'admin':        { sections: ['Financial', 'Gaming', 'Reports', 'Account'], gamingPaths: null },
  'common-user':  { sections: ['Financial', 'Gaming', 'Reports', 'Account'], gamingPaths: null },
  'crm-user':     { sections: ['Financial', 'Gaming', 'Reports', 'Account'], gamingPaths: null },
  'crm-support':  { sections: ['Gaming', 'Account'], gamingPaths: ['/transaction'] },
};

const ROLE_PRIORITY = ['super-admin', 'admin', 'common-user', 'crm-user', 'crm-support'];

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(getWithExpiry("token") !== null);
  const [sidebarContents, setSidebarContents] = useState([]);

  const roles = getWithExpiry('roles') ?? [];

  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  }

  const logout = async() => {
    var token = getWithExpiry("token");
    if (!token) {
      setLoggedIn(false);
    }
    var result = await logoutCall(token);
    if (result) {
      sessionStorage.clear();
      setLoggedIn(false);
    }
  }

  useEffect(() => {
    if (loggedIn) {
      
      if (!roles || roles.length === 0) { setLoggedIn(false); return; }
      const activeRole = ROLE_PRIORITY.find(role => roles.includes(role)) ?? 'crm-support';
      const access = ROLE_ACCESS[activeRole];

      const allGamingContents = [
        { linkEl: <Link to="/game-config">Game Config</Link>, path: "/game-config" },
        { linkEl: <Link to="/game-session">Game Session</Link>, path: "/game-session" },
        { linkEl: <Link to="/transaction">Transaction</Link>, path: "/transaction" },
        { linkEl: <Link to="/campaign">Campaign</Link>, path: "/campaign" },
        { linkEl: <Link to="/tenant">Tenant</Link>, path: "/tenant" },
        { linkEl: <Link to="/jackpot">Jackpot</Link>, path: "/jackpot" },
        { linkEl: <Link to="/campaign-player">Campaign Player</Link>, path: "/campaign-player" },
        { linkEl: <Link to="/campaign-operator-game-config">Campaign Games</Link>, path: "/campaign-operator-game-config" },
        { linkEl: <Link to="/freespin-offer">FreeSpin Offer</Link>, path: "/freespin-offer" },
        { linkEl: <Link to="/campaign-transaction">Campaign Transaction</Link>, path: "/campaign-transaction" },
      ];

      const visibleGamingContents = access.gamingPaths
        ? allGamingContents.filter(item => access.gamingPaths.includes(item.path))
        : allGamingContents;

      const allSections = [
        {
          submenuTitle: "Financial",
          submenuIcon: <FontAwesomeIcon icon={faSackDollar} />,
          contents: [
            {
              linkEl: <Link to="/currency-rate">Currency Rate</Link>,
              path: "/currency-rate"
            }
          ]
        },
        {
          submenuTitle: "Gaming",
          submenuIcon: <FontAwesomeIcon icon={faDiceSix} />,
          contents: visibleGamingContents,
        },
        {
          submenuTitle: "Reports",
          submenuIcon: <FontAwesomeIcon icon={faFile} />,
          contents: [
            {
              linkEl: <Link to="/game-report">Game Report</Link>,
              path: "/game-report"
            }
          ]
        },
        {
          submenuTitle: "Account",
          submenuIcon: <FontAwesomeIcon icon={faUser} />,
          contents: [
            {
              linkEl: <button onClick={logout}>Logout</button>,
              path: null
            }
          ]
        }
      ];

      setSidebarContents(
        allSections.filter(section => access.sections.includes(section.submenuTitle))
      );

      if(window.location.href.includes("login")) {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        let returnUrl = params.get('returnUrl');
        navigate(returnUrl ? returnUrl : "/currency-rate");
      }
    } else {
      setSidebarContents([
        {
          submenuTitle: "Account",
          submenuIcon: <FontAwesomeIcon icon={faUser} />,
          contents: [
            {
              linkEl: <Link to="/login">Login</Link>,
              path: "/login"
            }
          ]
        }
      ]);
      localStorage.removeItem("roles");
      if (!location.pathname.includes("login"))
        navigate(`/login?returnUrl=${location.pathname}${location.search}`);
    }
  }, [loggedIn]);

  return (
    <div id="wrapper" className={`${sidebarOpen ? "toggled" : ""}`}>
      <ToastContainer position="bottom-right" 
                    autoClose={10000}
                    theme="dark"
                    />
      <div className='overlay'></div>
      <UISidebar contents={sidebarContents} logoSrc={logo} />
      <div id="page-content-wrapper">
        <UINavbar toggleSidebar={toggleSidebar} />
        <div className='container app-container'>
          <Routes>
            <Route path='/' element={<></>} />

            <Route path='/login' element={<LoginPage setLoggedIn={setLoggedIn} />} />

            <Route path='/currency-rate' element={<CurrencyRateListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/currency-rate/details' element={<CurrencyRateDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/game-config' element={<GameConfigListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/game-config/details' element={<GameConfigDetailsPage setLoggedIn={setLoggedIn} />} />
            
            <Route path="/game-session" element={<GameSessionListPage setLoggedIn={setLoggedIn} />} />
            <Route path="/game-session/details" element={<GameSessionDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/transaction' element={<TransactionListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/transaction/details' element={<TransactionDetailsPage displayIds={["round_id", "provider_transaction_id", "operator_transaction_id"]}  setLoggedIn={setLoggedIn} />} />

            <Route path='/campaign' element={<CampaignListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/campaign/details' element={<CampaignDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/tenant' element={<TenantListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/tenant/details' element={<TenantDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/jackpot' element={<JackpotListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/jackpot/details' element={<JackpotDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/campaign-operator-game-config' element={<CampaignOperatorGameConfigListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/campaign-operator-game-config/details' element={<CampaignOperatorGameConfigDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/freespin-offer' element={<FreeSpinOfferListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/freespin-offer/details' element={<FreeSpinOfferDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/campaign-transaction' element={<CampaignPlayerCurrencyTransactionListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/campaign-transaction/details' element={<CampaignPlayerCurrencyTransactionDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/campaign-player' element={<CampaignPlayerListPage setLoggedIn={setLoggedIn} />} />
            <Route path='/campaign-player/details' element={<CampaignPlayerDetailsPage setLoggedIn={setLoggedIn} />} />

            <Route path='/game-report' element={<GameReportPage setLoggedIn={setLoggedIn} />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
